import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header, Footer } from '../../../../shared/components';
import { authService } from '../../services/auth/authService';
import { getOnboardingStatus } from '../../services/doctor-onboarding/doctorOnboardingService';
import '../../../../shared/styles/common.css';
import '../../../../shared/components/Header/Header.css';
import '../../../../shared/components/Footer/Footer.css';
import './LoginPage.css'; /* for .login-brand, .login-logo-img (logo size) */
import './DoctorSignUpPage.css';

declare global {
  interface Window {
    google: any;
  }
}

/**
 * Doctor Sign Up - Step 1 (Account Creation).
 * Matches Android/iOS: email, password, Terms & Conditions, Privacy Policy.
 * API: POST /api/v1/doctor-auth/email/register
 */
const DoctorSignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<'form' | 'verification_pending'>('form');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  const formatError = (err: any, fallback: string) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => (typeof item?.msg === 'string' ? item.msg : JSON.stringify(item)))
        .filter(Boolean);
      if (messages.length) return messages.join(', ');
    }
    if (detail && typeof detail === 'object') {
      if (typeof detail.msg === 'string') return detail.msg;
      return JSON.stringify(detail);
    }
    return err?.message || fallback;
  };

  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const isPasswordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword;
  const isFormValid =
    isValidEmail(email) &&
    isPasswordValid &&
    passwordsMatch &&
    acceptTerms &&
    acceptPrivacy;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.registerDoctor(email, password);
      setRegisteredEmail(email);
      setStep('verification_pending');
    } catch (err: any) {
      setError(formatError(err, 'Registration failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const redirectAfterDoctorLogin = useCallback(async () => {
    try {
      const status = await getOnboardingStatus();
      const statusVal = (status.onboarding_status || '').toLowerCase();
      if (statusVal === 'approved') {
        navigate('/dashboard');
      } else if (statusVal === 'submitted' || statusVal === 'rejected') {
        navigate('/doctor-review-status');
      } else {
        navigate('/doctor-onboarding');
      }
    } catch {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleGoogleSignIn = useCallback(async (response: any) => {
    const credential = response.credential || response;
    if (!credential) {
      setError('Google sign-in failed. Please try again.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const loginResponse = await authService.loginWithGoogle(credential);
      authService.storeTokens(
        loginResponse.access_token,
        loginResponse.refresh_token,
        loginResponse.csrf_token
      );
      await redirectAfterDoctorLogin();
    } catch (err: any) {
      setError(formatError(err, 'Google sign-in failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [redirectAfterDoctorLogin]);

  useEffect(() => {
    if (!googleClientId) return;
    const initializeGoogleSignIn = () => {
      if (window.google && googleButtonRef.current) {
        window.google.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleSignIn,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          width: 'auto',
          text: 'signup_with',
          locale: 'en',
          shape: 'rectangular',
        });
      }
    };

    let checkInterval: NodeJS.Timeout | null = null;
    if (window.google) {
      initializeGoogleSignIn();
    } else {
      checkInterval = setInterval(() => {
        if (window.google) {
          if (checkInterval) clearInterval(checkInterval);
          initializeGoogleSignIn();
        }
      }, 100);
      setTimeout(() => {
        if (checkInterval) clearInterval(checkInterval);
      }, 10000);
    }
    return () => {
      if (checkInterval) clearInterval(checkInterval);
    };
  }, [googleClientId, handleGoogleSignIn]);

  const handleResendVerification = async () => {
    setError('');
    setResendLoading(true);
    try {
      const msg = await authService.resendDoctorVerificationEmail(registeredEmail);
      setError(''); // show success in info style if we add it
      alert(msg);
    } catch (err: any) {
      setError(
        err.response?.data?.detail || 'Failed to resend. Please try again.'
      );
    } finally {
      setResendLoading(false);
    }
  };

  if (step === 'verification_pending') {
    return (
      <div className="login-page-wrapper">
        <Header showDietitiansLink={false} />
        <div className="login-page">
          <div className="login-container doctor-signup-container">
            <div className="login-header">
              <div className="login-brand">
                <img src="/images/zivo_logo.png" alt="Zivo" className="login-logo-img" />
              </div>
              <h2 className="login-title">Verify your email</h2>
              <p className="login-subtitle">
                {registeredEmail ? (
                  <>We sent a verification link to <strong>{registeredEmail}</strong>. </>
                ) : (
                  <>Your account is under review. </>
                )}
                Click the link to verify your account (if required), then sign in after approval to complete your profile.
              </p>
            </div>
            {error && <div className="error-message">{error}</div>}
            <button
              type="button"
              className="login-button secondary"
              onClick={handleResendVerification}
              disabled={resendLoading}
            >
              {resendLoading ? 'Sending...' : 'Resend verification email'}
            </button>
            <Link to="/login" className="back-to-login-link">
              Back to Login
            </Link>
          </div>
        </div>
        <Footer variant="full" />
      </div>
    );
  }

  return (
    <div className="login-page-wrapper">
      <Header showDietitiansLink={false} />
      <div className="login-page">
        <div className="login-container doctor-signup-container">
          <div className="login-header">
            <div className="login-brand">
              <img src="/images/zivo_logo.png" alt="Zivo" className="login-logo-img" />
            </div>
            <h2 className="login-title">Doctor Sign Up</h2>
            <p className="login-subtitle">
              Create your account to get started. You will verify your email next.
            </p>
          </div>

          <div className="login-form-container">
            {googleClientId && (
              <>
                <div ref={googleButtonRef} className="google-signin-button"></div>
                <div className="divider">
                  <span className="divider-line"></span>
                  <span className="divider-text">or</span>
                  <span className="divider-line"></span>
                </div>
              </>
            )}
          <form onSubmit={handleSubmit} className="login-form doctor-signup-form">
            <div className="form-group">
              <label htmlFor="signup-email">Email</label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="signup-password">Password</label>
              <div className="password-input-container">
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  required
                  minLength={8}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {password && !isPasswordValid && (
                <span className="field-hint">Password must be at least 8 characters</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="signup-confirm-password">Confirm Password</label>
              <div className="password-input-container">
                <input
                  id="signup-confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide' : 'Show'}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <span className="field-hint">Passwords do not match</span>
              )}
            </div>

            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={loading}
                />
                <span>I agree to the <a href="/terms-and-conditions.html" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a></span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  disabled={loading}
                />
                <span>I agree to the <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a></span>
              </label>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="login-button"
              disabled={!isFormValid || loading}
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="signup-link">
            Already have an account? <Link to="/login">Log in</Link>
          </p>
          </div>
        </div>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default DoctorSignUpPage;
